import type { ReactNode } from 'react';
import { getDefaultThresholdFromConfig } from '@/features/settings/utils/resolveDashboardSetting';

export interface BudgetLawSplitProps {
  abPercent: number;
  cPercent: number;
  abTotalUSD: number;
  cTotalUSD: number;
  compliant: boolean;
  currency?: 'USD';
  className?: string;
  /** A+B minimum as fraction 0–1 (default from Dashboard Settings). */
  minimumFraction?: number;
  /** Amber band lower bound as fraction 0–1 (default from Dashboard Settings). */
  warningFraction?: number;
  settingSourceNote?: ReactNode;
}

const AB_OK = '#15803d';
const AB_WARN = '#d97706';
const AB_FAIL = '#b91c1c';
const C_NEUTRAL = '#94a3b8';

function defaultMinimumFraction(): number {
  return getDefaultThresholdFromConfig('Budget Law A+B Minimum Percent') / 100;
}

function defaultWarningFraction(): number {
  return getDefaultThresholdFromConfig('Budget Law A+B Warning Band Percent') / 100;
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.min(1, Math.max(0, n));
}

function formatPercent01(value: number, fractionDigits = 1): string {
  if (!Number.isFinite(value)) return '0.0%';
  return `${(clamp01(value) * 100).toFixed(fractionDigits)}%`;
}

function formatUSD(amount: number, currency: 'USD'): string {
  if (!Number.isFinite(amount)) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

function segmentTooltip(
  label: string,
  fraction: number,
  usd: number,
  currency: 'USD'
): string {
  return `${label}: ${formatPercent01(fraction, 1)} · ${formatUSD(usd, currency)}`;
}

function abSegmentColor(ab: number, minimumFraction: number, warningFraction: number): string {
  if (ab >= minimumFraction - 0.0005) return AB_OK;
  if (ab >= warningFraction - 0.0005) return AB_WARN;
  return AB_FAIL;
}

/**
 * Governed budget split bar with configurable A+B minimum and warning band.
 */
export function BudgetLawSplit({
  abPercent,
  cPercent,
  abTotalUSD,
  cTotalUSD,
  compliant,
  currency = 'USD',
  className = '',
  minimumFraction = defaultMinimumFraction(),
  warningFraction = defaultWarningFraction(),
  settingSourceNote,
}: BudgetLawSplitProps) {
  const minFrac = clamp01(minimumFraction);
  const warnFrac = clamp01(warningFraction);
  const minPct = Math.round(minFrac * 100);
  const ab = clamp01(abPercent);
  const c = clamp01(cPercent);
  const sum = ab + c;
  const empty = sum <= 0 || !Number.isFinite(sum);

  const meetsLaw = ab >= minFrac - 0.0005;
  const abColor = abSegmentColor(ab, minFrac, warnFrac);
  const statusLabel = meetsLaw
    ? `Compliant (A+B ≥ ${minPct}%)`
    : ab >= warnFrac - 0.0005
      ? 'Below threshold — review A+B share'
      : `Non-compliant — A+B share under ${minPct}% rule`;

  if (empty) {
    return (
      <div
        className={`rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-4 py-8 text-center text-sm text-slate-500 print:py-3 print:text-[8px] ${className}`}
        role="status"
        aria-live="polite"
      >
        Split unavailable
      </div>
    );
  }

  const abWidthPct = ab * 100;
  const cWidthPct = c * 100;

  const ariaLabel = `Budget law split. A plus B ${formatPercent01(ab, 1)}, total ${formatUSD(abTotalUSD, currency)}. C ${formatPercent01(c, 1)}, total ${formatUSD(cTotalUSD, currency)}. ${compliant ? 'Flagged compliant.' : ''} ${statusLabel}.`;

  return (
    <div
      className={`rounded-xl border border-slate-300/90 bg-gradient-to-b from-slate-100 to-white p-4 shadow-md ring-1 ring-slate-200/80 print:border-slate-400 print:p-2 print:shadow-none ${className}`}
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-800">
            Governed budget split ({minPct}% / {100 - minPct}% rule)
          </h4>
          <p className="mt-0.5 text-[11px] font-medium text-slate-600">{statusLabel}</p>
        </div>
        <span
          className={`inline-flex w-fit shrink-0 items-center rounded-md px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ring-1 print:text-[7px] ${
            meetsLaw
              ? 'bg-emerald-100 text-emerald-900 ring-emerald-200'
              : 'bg-amber-50 text-amber-900 ring-amber-200'
          }`}
        >
          A+B {formatPercent01(ab, 0)}
        </span>
      </div>

      <p className="mt-3 flex flex-col gap-1 text-xs font-semibold text-slate-800 sm:flex-row sm:flex-wrap sm:gap-x-6 print:text-[8px]">
        <span>
          A+B: {formatPercent01(ab, 1)} ({formatUSD(abTotalUSD, currency)})
        </span>
        <span>
          C: {formatPercent01(c, 1)} ({formatUSD(cTotalUSD, currency)})
        </span>
      </p>

      <div className="mt-4 print:mt-2">
        <div className="mb-1 flex justify-between text-[10px] font-bold uppercase tracking-wide text-slate-500 print:text-[7px]">
          <span>0%</span>
          <span className="text-slate-700">{minPct}% minimum (A+B)</span>
          <span>100%</span>
        </div>
        <div
          className="relative flex h-12 w-full overflow-hidden rounded-lg bg-slate-200 ring-2 ring-slate-300/90 print:h-8"
          role="img"
          aria-label={ariaLabel}
        >
          <div
            className="h-full shrink-0 transition-[width] duration-300 ease-out"
            style={{
              width: `${abWidthPct}%`,
              backgroundColor: abColor,
            }}
            title={segmentTooltip('A+B', ab, abTotalUSD, currency)}
          />
          <div
            className="h-full shrink-0 transition-[width] duration-300 ease-out"
            style={{
              width: `${cWidthPct}%`,
              backgroundColor: C_NEUTRAL,
            }}
            title={segmentTooltip('C', c, cTotalUSD, currency)}
          />
          <span
            className="pointer-events-none absolute top-0 z-10 h-full w-0.5 bg-slate-950 shadow-[2px_0_0_rgba(255,255,255,0.5)]"
            style={{ left: `${minPct}%`, transform: 'translateX(-50%)' }}
            aria-hidden
          />
        </div>
        <p className="mt-1.5 text-[10px] font-medium text-slate-500 print:text-[7px]">
          Vertical marker: minimum A+B share required under the governed rule. C segment shown in neutral grey.
        </p>
      </div>
      {settingSourceNote ? <div className="mt-3">{settingSourceNote}</div> : null}
    </div>
  );
}
